import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("sign") || s.includes("active") || s.includes("final")) return "success";
  if (s.includes("pending") || s.includes("partial") || s.includes("review")) return "warning";
  if (s.includes("cancel") || s.includes("reject") || s.includes("supersed") || s.includes("expir")) return "error";
  return "default";
};

/** Shared contract status summary card (Phase 7.8). */
export const ContractStatusCard = ({
  title = "وضعیت قرارداد",
  contractNumber,
  versionNumber,
  status,
  statusTitle,
  projectCode,
  verificationCode,
}) => (
  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid="contract-status-card">
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Typography variant="subtitle1">{title}</Typography>
      <Chip size="small" color={statusColor(status)} label={statusTitle || status || "—"} />
    </Stack>
    <Stack spacing={0.5} mt={1.5}>
      {contractNumber ? <Typography variant="body2">شماره: {contractNumber}</Typography> : null}
      {versionNumber != null ? <Typography variant="body2">نسخه: {versionNumber}</Typography> : null}
      {projectCode ? <Typography variant="body2">کد پروژه: {projectCode}</Typography> : null}
      {verificationCode ? (
        <Typography variant="body2" sx={{ direction: "ltr", unicodeBidi: "isolate" }}>
          کد تأیید: {verificationCode}
        </Typography>
      ) : null}
    </Stack>
  </Box>
);

export const ContractPartiesCard = ({ parties = [], title = "طرفین قرارداد" }) => (
  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid="contract-parties-card">
    <Typography variant="subtitle1" mb={1}>
      {title}
    </Typography>
    <Stack spacing={1}>
      {parties.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          طرفی ثبت نشده است.
        </Typography>
      ) : (
        parties.map((p, idx) => (
          <Typography key={idx} variant="body2">
            {p.roleTitleFa || p.roleCode}: {p.displayNameMasked || p.name || "—"}
          </Typography>
        ))
      )}
    </Stack>
  </Box>
);

export const ContractDocumentViewer = ({ title = "سند قرارداد", children, emptyMessage = "سندی برای نمایش نیست." }) => (
  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid="contract-document-viewer">
    <Typography variant="subtitle1" mb={1}>
      {title}
    </Typography>
    {children || (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    )}
  </Box>
);

export const ContractSignatureStepper = ({ steps = [] }) => (
  <Stack spacing={1} data-testid="contract-signature-stepper">
    {steps.map((step, idx) => (
      <Stack key={idx} direction="row" spacing={1} alignItems="center">
        <Chip size="small" label={idx + 1} />
        <Typography variant="body2">
          {step.label || step.signerRoleCode || "امضاکننده"} — {step.statusTitle || step.status || "—"}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export const ContractOtpDialog = ({
  open,
  maskedMobile,
  otpCode,
  onOtpChange,
  onSubmit,
  onCancel,
  loading,
  OtpInput,
}) => {
  if (!open) return null;
  return (
    <Box sx={{ p: 2, border: "1px dashed", borderColor: "warning.main", borderRadius: 1 }} data-testid="contract-otp-dialog">
      <Alert severity="info" sx={{ mb: 1 }}>
        کد تأیید به {maskedMobile || "شماره شما"} ارسال شد.
      </Alert>
      {OtpInput ? (
        <OtpInput value={otpCode} onChange={onOtpChange} />
      ) : (
        <input
          data-testid="contract-otp-input"
          value={otpCode || ""}
          onChange={(e) => onOtpChange?.(e.target.value)}
          style={{ width: "100%", padding: 8, marginBottom: 8 }}
        />
      )}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={loading} onClick={onSubmit}>
          تأیید و امضا
        </Button>
        <Button variant="text" disabled={loading} onClick={onCancel}>
          انصراف
        </Button>
      </Stack>
    </Box>
  );
};

export const ContractTimeline = ({ events = [], title = "جدول زمانی" }) => (
  <Box data-testid="contract-timeline">
    <Typography variant="subtitle1" mb={1}>
      {title}
    </Typography>
    <Stack spacing={1}>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          رویدادی ثبت نشده است.
        </Typography>
      ) : (
        events.map((e, idx) => (
          <Typography key={idx} variant="body2">
            {e.titleFa || e.eventCode} — {e.occurredAt || e.at || ""}
          </Typography>
        ))
      )}
    </Stack>
  </Box>
);

export const ContractVerificationCard = ({
  verificationCode,
  isValid,
  message,
  pdfSha256,
  contractTypeTitleFa,
}) => (
  <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }} data-testid="contract-verification-card">
    <Typography variant="subtitle1">استعلام قرارداد</Typography>
    {contractTypeTitleFa ? <Typography variant="body2">{contractTypeTitleFa}</Typography> : null}
    <Chip
      size="small"
      sx={{ mt: 1 }}
      color={isValid ? "success" : "default"}
      label={isValid ? "معتبر" : "نامعتبر / ناقص"}
    />
    {message ? (
      <Typography variant="body2" mt={1}>
        {message}
      </Typography>
    ) : null}
    {verificationCode ? (
      <Typography variant="body2" sx={{ direction: "ltr", unicodeBidi: "isolate", mt: 1 }}>
        {verificationCode}
      </Typography>
    ) : null}
    {pdfSha256 ? (
      <Typography variant="caption" display="block" sx={{ direction: "ltr", unicodeBidi: "isolate", mt: 0.5 }}>
        SHA256: {pdfSha256}
      </Typography>
    ) : null}
  </Box>
);

export const ContractDownloadActions = ({
  canDownloadDraft,
  canDownloadFinal,
  onDownloadDraft,
  onDownloadFinal,
  loading,
}) => (
  <Stack direction="row" spacing={1} data-testid="contract-download-actions">
    {canDownloadDraft ? (
      <Button variant="outlined" disabled={loading} onClick={onDownloadDraft}>
        دانلود پیش‌نویس
      </Button>
    ) : null}
    {canDownloadFinal ? (
      <Button variant="contained" disabled={loading} onClick={onDownloadFinal}>
        دانلود نسخه نهایی
      </Button>
    ) : null}
  </Stack>
);

export const ContractEvidenceSummary = ({ items = [] }) => (
  <Box data-testid="contract-evidence-summary">
    <Typography variant="subtitle1" mb={1}>
      شواهد امضا
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        شاهدی ثبت نشده است.
      </Typography>
    ) : (
      <Stack spacing={0.5}>
        {items.map((item, idx) => (
          <Typography key={idx} variant="body2">
            {item.signerRoleCode || item.role}: {item.signedAt || "—"}
          </Typography>
        ))}
      </Stack>
    )}
  </Box>
);

export const ContractVersionHistory = ({ versions = [] }) => (
  <Box data-testid="contract-version-history">
    <Typography variant="subtitle1" mb={1}>
      تاریخچه نسخه‌ها
    </Typography>
    <Stack spacing={0.5}>
      {versions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          نسخه‌ای ثبت نشده است.
        </Typography>
      ) : (
        versions.map((v, idx) => (
          <Typography key={idx} variant="body2">
            V{v.versionNumber || v.version} — {v.statusTitle || v.status}
            {v.isActive ? " (فعال)" : ""}
            {v.isSuperseded ? " (جایگزین شده)" : ""}
          </Typography>
        ))
      )}
    </Stack>
  </Box>
);

export default {
  ContractStatusCard,
  ContractPartiesCard,
  ContractDocumentViewer,
  ContractSignatureStepper,
  ContractOtpDialog,
  ContractTimeline,
  ContractVerificationCard,
  ContractDownloadActions,
  ContractEvidenceSummary,
  ContractVersionHistory,
};
